import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { FacetCombobox, FacetComboboxOption } from '@/components/ui/facet-combobox';
import { useI18n } from '@/hooks/useI18n';

export type SharedLinkStatusFilter = 'all' | 'active' | 'expired';

interface SharedLinksFiltersProps {
  status: SharedLinkStatusFilter;
  onStatusChange: (status: SharedLinkStatusFilter) => void;

  /**
   * Designer presenti fra i link visibili. Assente per chi vede solo i propri link:
   * filtrerebbe su un elenco col suo solo nome (stessa scelta di `PaymentsFilters`).
   */
  designerOptions?: FacetComboboxOption[];
  designerEmail: string | null;
  onDesignerChange: (email: string | null) => void;

  search: string;
  onSearchChange: (value: string) => void;
}

/**
 * Riga filtri dei link condivisi, con la stessa grammatica della dashboard pagamenti e
 * della libreria: prima le tendine, poi un tratto verticale, poi la ricerca libera.
 *
 * Qui la ricerca era in testa e larga, come se fosse il modo principale di restringere.
 * Non lo è: le tendine filtrano su valori scelti da un elenco chiuso e coprono i casi
 * normali, la ricerca libera è il ripiego trasversale di chi in tendina non trova la voce.
 * L'ordine ora lo dice, e il separatore segna il confine fra i due modi senza etichette.
 */
export function SharedLinksFilters({
  status,
  onStatusChange,
  designerOptions,
  designerEmail,
  onDesignerChange,
  search,
  onSearchChange,
}: SharedLinksFiltersProps) {
  const { t } = useI18n();

  // Le liste sono complete (i link sono già tutti in memoria): nessuna ricerca lato
  // server, cmdk filtra in memoria.
  const shared = {
    clearLabel: t('shared.filtersClear'),
    emptyLabel: t('shared.filtersNoOptions'),
    className: 'min-w-[160px]',
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      {/* 'all' non è un valore selezionato ma l'assenza di filtro: per FacetCombobox è
          `null`, e il placeholder ("Tutti gli stati") lo dice già. */}
      <FacetCombobox
        {...shared}
        options={[
          { value: 'active', label: t('shared.active') },
          { value: 'expired', label: t('shared.expired') },
        ]}
        value={status === 'all' ? null : status}
        onChange={(value) => onStatusChange((value as SharedLinkStatusFilter) ?? 'all')}
        placeholder={t('shared.allStatus')}
        searchPlaceholder={t('shared.status')}
      />

      {designerOptions && (
        <FacetCombobox
          {...shared}
          options={designerOptions}
          value={designerEmail}
          onChange={onDesignerChange}
          placeholder={t('shared.allDesigners')}
          searchPlaceholder={t('shared.searchDesigner')}
        />
      )}

      <span className="w-px h-6 bg-gray-200 mx-1" aria-hidden="true" />

      <div className="relative w-[200px]">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        <Input
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={t('common.search')}
          className="h-9 pl-8 text-sm"
        />
      </div>
    </div>
  );
}
